import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

import { Reveal, MaskLines, MaskLinesInView } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { MenuExperience } from "@/components/MenuExperience";
import { EventForm } from "@/components/EventForm";
import { GoogleReviewsBand } from "@/components/GoogleReviewsBand";
import { riverStone, experiences, signatures, eventTypes, IMG, sisterBrands } from "@/data/brand";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <main data-testid="home-page">
      {/* ============ 1. CINEMATIC HERO ============ */}
      <section ref={heroRef} className="relative h-[100svh] overflow-hidden bg-black">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={IMG.hero} alt="River Stone Chophouse interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/60" />
        </motion.div>

        <motion.div style={{ y: heroTextY, opacity: heroOpacity }} className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 pt-28 md:pt-32">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
            className="text-[10px] md:text-xs tracking-[0.5em] uppercase text-[#c5a059] mb-8"
          >
            {riverStone.location.region}
          </motion.p>

          <h1 className="font-serif text-white font-light leading-[0.95] text-5xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
            <MaskLines lines={["RIVER STONE"]} delay={0.4} />
            <MaskLines lines={["CHOPHOUSE"]} delay={0.55} lineClassName="text-[#c5a059] italic" />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
            className="mt-8 flex flex-col items-center"
          >
            <p className="font-serif italic text-2xl md:text-3xl text-white/90">{riverStone.tagline}</p>
            <p className="text-[11px] md:text-sm tracking-[0.35em] uppercase text-white/60 mt-4">{riverStone.kicker}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 1 }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <LuxButton to="/reservations" variant="gold" testid="hero-reserve-btn">Reserve a Table</LuxButton>
            <LuxButton to="/menu" variant="outline-light" testid="hero-menu-btn">View Menu</LuxButton>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <button
            type="button"
            data-testid="hero-scroll-down"
            aria-label="Scroll to content"
            onClick={() => {
              const el = document.getElementById("experience");
              if (el && window.lenis) window.lenis.scrollTo(el, { offset: -80 });
              else el?.scrollIntoView({ behavior: "smooth" });
            }}
            className="text-white/50 hover:text-[#c5a059] transition-colors cursor-pointer p-2"
          >
            <ChevronDown className="animate-bounce" size={22} />
          </button>
        </motion.div>
      </section>

      {/* ============ 2. THE RIVER STONE EXPERIENCE ============ */}
      <section id="experience" className="bg-[#f8f5f0] py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#C1272D]">A Standard All Its Own</span>
            </Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1a1817] font-light leading-[1.05] mt-6">
              <MaskLinesInView lines={["The River Stone", "Experience"]} />
            </h2>
            <Reveal delay={0.2}>
              <p className="font-serif italic text-2xl text-[#9E1F24] mt-6">Coastal Virginia&rsquo;s Premier Chophouse</p>
              <p className="text-[#6d6661] font-light leading-relaxed mt-6 max-w-md">{riverStone.intro}</p>
              <div className="mt-10">
                <Link to="/our-story" data-testid="experience-story-link" className="group inline-flex items-center gap-3 text-[#1a1817] text-xs tracking-[0.2em] uppercase border-b border-[#C1272D] pb-1.5 hover:gap-5 transition-all duration-300">
                  Our Story <ArrowRight size={16} className="text-[#C1272D]" />
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15} className="relative">
              <div className="overflow-hidden">
                <motion.img
                  src={IMG.fireplace} alt="River Stone dining room"
                  className="w-full h-[380px] md:h-[560px] object-cover"
                  initial={{ scale: 1.2 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0a0908] text-white px-8 py-6 hidden md:block">
                <p className="font-serif text-4xl text-[#c5a059]">Est. 2008</p>
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/60 mt-1">Harbour View, Suffolk</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ 3. SIGNATURE EXPERIENCES ============ */}
      <section id="signatures" className="bg-[#0a0908] py-24 md:py-36 grain relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-16 md:mb-20">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">The Art of the Chophouse</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light mt-5">Signature Experiences</h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {experiences.map((exp, i) => (
              <Reveal key={exp.title} delay={i * 0.12}>
                <div className="group relative overflow-hidden aspect-[3/4] cursor-default">
                  <img src={exp.image} alt={exp.title} className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute inset-0 p-8 flex flex-col justify-end">
                    <span className="font-serif text-5xl text-[#c5a059]/70">{exp.n}</span>
                    <h3 className="font-serif text-3xl text-white mt-3">{exp.title}</h3>
                    <p className="text-[11px] tracking-[0.2em] uppercase text-[#c5a059] mt-2">{exp.subtitle}</p>
                    <p className="text-white/60 text-sm font-light mt-4 max-h-0 opacity-0 group-hover:max-h-40 group-hover:opacity-100 overflow-hidden transition-all duration-500">
                      {exp.copy}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 4. FEATURED DISHES / SIGNATURES ============ */}
      <section className="bg-[#f8f5f0] py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <Reveal>
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#C1272D]">Made for the Occasion</span>
              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1a1817] font-light mt-5">Signatures</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <LuxButton to="/menu" variant="outline-dark" testid="signatures-menu-btn">Explore the Menu</LuxButton>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {signatures.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.1}>
                <div className="group">
                  <div className="overflow-hidden aspect-[4/5]">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-[#1a1817] mt-5">{s.name}</h3>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#6d6661] mt-1.5">{s.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 5. MENU EXPERIENCE ============ */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-14">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#C1272D]">Where the Evening Begins</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1a1817] font-light mt-5">The Menu</h2>
            <p className="text-[#6d6661] font-light mt-4 max-w-md mx-auto">A living taste of the kitchen. Select a course to explore.</p>
          </Reveal>
          <Reveal delay={0.15}><MenuExperience /></Reveal>
          <div className="text-center mt-16">
            <LuxButton to="/menu" variant="outline-dark" testid="home-view-full-menu">View Full Menu</LuxButton>
          </div>
        </div>
      </section>

      {/* ============ REVIEWS BAND ============ */}
      <GoogleReviewsBand />

      {/* ============ 6. PRIVATE DINING & EVENTS ============ */}
      <section id="private" className="relative bg-[#0a0908] text-white">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[400px] lg:min-h-[720px] overflow-hidden">
            <motion.img
              src={IMG.privateDining} alt="Private dining room"
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.5 }}
            />
            <div className="absolute inset-0 bg-black/20" />
          </div>
          <div className="flex items-center py-20 lg:py-0 px-6 md:px-16">
            <div className="max-w-xl">
              <Reveal>
                <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">Private Dining & Events</span>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mt-6 leading-[1.05]">
                  <MaskLinesInView lines={["Celebrate at", "River Stone"]} />
                </h2>
                <p className="text-white/60 font-light leading-relaxed mt-6">
                  From intimate dinners and corporate meetings to rehearsal dinners, celebrations and receptions, River Stone offers distinctive private dining experiences — with configurations accommodating events of up to approximately 250 guests.
                </p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3 mt-8">
                  {eventTypes.map((e) => (
                    <div key={e} className="flex items-center gap-3 text-sm text-white/70 font-light">
                      <span className="w-1 h-1 bg-[#c5a059] rounded-full" />{e}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mt-10">
                  <LuxButton to="/private-dining" variant="gold" testid="home-private-dining-btn">Explore Private Dining</LuxButton>
                  <LuxButton to="/private-dining#events" variant="outline-light" testid="home-plan-event-btn">Plan Your Event</LuxButton>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 7. PRIVATE EVENT LEAD FORM ============ */}
      <section id="inquiry" className="bg-[#161412] py-24 md:py-32 grain relative">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-12">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">Begin the Conversation</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-white font-light mt-5">Plan Your Event</h2>
          </Reveal>
          <Reveal delay={0.15}><EventForm /></Reveal>
        </div>
      </section>

      {/* ============ 8. THE LOUNGE / HAPPY HOUR ============ */}
      <section id="lounge" className="relative min-h-[600px] flex items-center overflow-hidden">
        <img src={IMG.cocktail} alt="The Lounge" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full py-24">
          <div className="max-w-lg">
            <Reveal>
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">The Lounge</span>
              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white font-light mt-5 leading-none">Happy Hour</h2>
              <div className="flex items-center gap-6 mt-8">
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-white/50">When</p>
                  <p className="font-serif text-2xl text-white mt-1">{riverStone.happyHour.days}</p>
                  <p className="font-serif text-2xl text-[#c5a059]">{riverStone.happyHour.time}</p>
                </div>
              </div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/70 mt-8">{riverStone.happyHour.highlight}</p>
              <div className="mt-10">
                <LuxButton to="/menu" variant="outline-light" testid="lounge-bar-btn">Explore the Bar</LuxButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ 9. RESERVATION CTA ============ */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <motion.img
          src={IMG.reservationTable} alt="Your table awaits"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.2 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 2 }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-6">
          <Reveal>
            <h2 className="font-serif text-5xl sm:text-6xl md:text-8xl text-white font-light leading-[0.95]">
              <MaskLinesInView lines={["Your table", "is waiting."]} />
            </h2>
            <p className="font-serif italic text-xl md:text-2xl text-white/80 mt-6">Experience River Stone Chophouse.</p>
            <div className="mt-10">
              <LuxButton to="/reservations" variant="gold" testid="cta-reserve-btn" className="px-14 py-5">Reserve Your Table</LuxButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ 11. SISTER RESTAURANTS ============ */}
      <section className="bg-white py-16 md:py-20 border-t border-black/5">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-12 md:mb-14">
            <p className="text-base md:text-lg font-semibold text-[#1a1817] tracking-wide">A Pack Brothers Hospitality Restaurant</p>
            <p className="font-serif italic text-xl md:text-2xl text-[#6d6661] mt-1">Please visit our sister restaurants:</p>
          </Reveal>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-8 md:gap-x-10">
            {sisterBrands.map((b, i) => (
              <Reveal key={b.name} delay={(i % 8) * 0.06}>
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  data-testid={`sister-brand-${i}`}
                  title={b.name}
                  className="flex items-center justify-center px-2 opacity-90 hover:opacity-100 transition-all duration-500 hover:scale-105"
                >
                  <img
                    src={b.logo}
                    alt={b.name}
                    loading="lazy"
                    style={{ maxHeight: `${b.h}px` }}
                    className="w-auto max-w-[180px] object-contain"
                  />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
